/**
 * Setup-journey proposal shapes — the JSON stored per section on the event's
 * SetupPlan (`SetupPlanSection.proposal`) and consumed by the per-section
 * creators when the user clicks "Build" in the Setup Hub.
 *
 * Proposals are composed at Create time with all i18n keys RESOLVED to real
 * text (the creator no longer needs the `directionPacks` namespace), so Velo
 * and the hub can display and edit them as-is. Parsers below are hostile-input
 * tolerant — Velo may rewrite a proposal via update_setup_section, and a
 * malformed one must degrade to "nothing to build", never crash the hub.
 */
import type { EmailTrigger } from "@/types/enums/EmailTrigger";
import type { WorkflowSchedule } from "@/types/Workflow";
import type { WebsiteThemeId } from "@/types/WebsiteTheme";
import type { BookingMode } from "@/lib/slotting/types";
import type { SetupSectionKey } from "@/types/SetupPlan";
import type { EventDirection, PackageToggles } from "./types";

type TranslationFn = (key: string) => string;

export interface EmailsProposalItem {
  /** Stable pack-local key — workflow proposals reference it via emailKey. */
  key: string;
  /** EMAIL_TEMPLATE_PRESETS id supplying the design; must exist at build time. */
  presetId: string;
  name: string;
  subject: string;
}

export interface EmailsProposal {
  emails: EmailsProposalItem[];
}

export interface WorkflowsProposalItem {
  key: string;
  name: string;
  /** EmailsProposalItem.key whose created template the SEND_EMAIL action uses. */
  emailKey: string;
  triggers: EmailTrigger[];
  schedule?: Pick<WorkflowSchedule, "mode" | "anchor" | "offsetMinutes">;
  /** SCHEDULED broadcasts stay disabled by design; event-driven default true. */
  enabled: boolean;
}

export interface WorkflowsProposal {
  workflows: WorkflowsProposalItem[];
}

export interface OfferingProposal {
  name: string;
  bookingMode: BookingMode;
  treatments: Array<{ name: string; durationMinutes: number; capacity: number }>;
  lanes: Array<{ name: string; color: string }>;
}

export interface WebsiteProposal {
  themeId: WebsiteThemeId;
}

/**
 * Resolve a direction's pack into per-section proposal JSON strings,
 * honouring the package toggles. GOALS/GUESTS never carry a proposal.
 */
export function composeSectionProposals(
  direction: EventDirection,
  toggles: PackageToggles,
  tPack: TranslationFn,
): Partial<Record<SetupSectionKey, string>> {
  const out: Partial<Record<SetupSectionKey, string>> = {};

  if (toggles.emails && direction.pack.emails?.length) {
    const proposal: EmailsProposal = {
      emails: direction.pack.emails.map((email) => ({
        key: email.key,
        presetId: email.presetId,
        name: tPack(email.nameKey),
        subject: tPack(email.subjectKey),
      })),
    };
    out.EMAILS = JSON.stringify(proposal);
  }

  // Workflows only ship alongside the emails they reference: with the emails
  // toggle OFF, a WORKFLOWS proposal could never be built (its emailKeys have
  // no template source) — emitting it would strand the section (audit 2026-07-08).
  if (toggles.workflows && toggles.emails && direction.pack.emails?.length && direction.pack.workflows?.length) {
    const proposal: WorkflowsProposal = {
      workflows: direction.pack.workflows.map((wf) => ({
        key: wf.key,
        name: tPack(wf.nameKey),
        emailKey: wf.emailKey,
        triggers: wf.triggers,
        ...(wf.schedule ? { schedule: wf.schedule } : {}),
        enabled: wf.enabled,
      })),
    };
    out.WORKFLOWS = JSON.stringify(proposal);
  }

  if (toggles.appointments && direction.pack.offering) {
    const def = direction.pack.offering;
    const proposal: OfferingProposal = {
      name: tPack(def.nameKey),
      bookingMode: def.bookingMode,
      treatments: def.treatments.map((tr) => ({
        name: tPack(tr.nameKey),
        durationMinutes: tr.durationMinutes,
        capacity: tr.capacity,
      })),
      lanes: def.lanes.map((lane) => ({ name: tPack(lane.nameKey), color: lane.color })),
    };
    out.OFFERING = JSON.stringify(proposal);
  }

  if (toggles.website && direction.pack.website) {
    const proposal: WebsiteProposal = { themeId: direction.themeId };
    out.WEBSITE = JSON.stringify(proposal);
  }

  return out;
}

// ── hostile-input-tolerant parsers ──────────────────────────────────

function parseJson(raw: string | undefined | null): unknown {
  if (!raw || !raw.trim()) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

const isNonEmptyString = (v: unknown): v is string => typeof v === "string" && v.trim().length > 0;

export function parseEmailsProposal(raw: string | undefined | null): EmailsProposal | null {
  const parsed = parseJson(raw) as { emails?: unknown } | null;
  if (!parsed || !Array.isArray(parsed.emails)) return null;
  const emails: EmailsProposalItem[] = [];
  for (const item of parsed.emails as Array<Record<string, unknown>>) {
    if (!item || typeof item !== "object") continue;
    if (
      !isNonEmptyString(item.key) ||
      !isNonEmptyString(item.presetId) ||
      !isNonEmptyString(item.name) ||
      !isNonEmptyString(item.subject)
    ) {
      continue;
    }
    emails.push({
      key: item.key,
      presetId: item.presetId,
      name: item.name.slice(0, 200),
      subject: item.subject.slice(0, 300),
    });
  }
  return emails.length ? { emails } : null;
}

export function parseWorkflowsProposal(raw: string | undefined | null): WorkflowsProposal | null {
  const parsed = parseJson(raw) as { workflows?: unknown } | null;
  if (!parsed || !Array.isArray(parsed.workflows)) return null;
  const workflows: WorkflowsProposalItem[] = [];
  for (const item of parsed.workflows as Array<Record<string, unknown>>) {
    if (!item || typeof item !== "object") continue;
    if (!isNonEmptyString(item.name) || !isNonEmptyString(item.emailKey)) continue;
    if (!Array.isArray(item.triggers) || !item.triggers.every(isNonEmptyString)) continue;
    const triggers = item.triggers as EmailTrigger[];
    // Drafts-only invariant, ENFORCED AT BUILD TIME (audit 2026-07-08): a
    // SCHEDULED broadcast is always created disabled — the proposal JSON is
    // model-writable (update_setup_section), so `enabled:true` on a scheduled
    // mass-mail must never survive parsing, whatever the stored JSON says.
    const scheduled = triggers.includes("SCHEDULED" as EmailTrigger);
    workflows.push({
      key: isNonEmptyString(item.key) ? item.key : item.name,
      name: item.name.slice(0, 200),
      emailKey: item.emailKey,
      triggers,
      ...(item.schedule && typeof item.schedule === "object"
        ? { schedule: item.schedule as WorkflowsProposalItem["schedule"] }
        : {}),
      enabled: !scheduled && item.enabled === true,
    });
  }
  return workflows.length ? { workflows } : null;
}

export function parseOfferingProposal(raw: string | undefined | null): OfferingProposal | null {
  const parsed = parseJson(raw) as Record<string, unknown> | null;
  if (!parsed || !isNonEmptyString(parsed.name)) return null;
  if (!isNonEmptyString(parsed.bookingMode)) return null;
  const treatments = Array.isArray(parsed.treatments)
    ? (parsed.treatments as Array<Record<string, unknown>>)
        .filter(
          (tr) =>
            tr &&
            isNonEmptyString(tr.name) &&
            typeof tr.durationMinutes === "number" &&
            tr.durationMinutes > 0 &&
            typeof tr.capacity === "number" &&
            tr.capacity > 0,
        )
        .map((tr) => ({
          name: (tr.name as string).slice(0, 200),
          durationMinutes: tr.durationMinutes as number,
          capacity: tr.capacity as number,
        }))
    : [];
  const lanes = Array.isArray(parsed.lanes)
    ? (parsed.lanes as Array<Record<string, unknown>>)
        .filter((lane) => lane && isNonEmptyString(lane.name))
        .map((lane) => ({
          name: (lane.name as string).slice(0, 200),
          color: isNonEmptyString(lane.color) ? lane.color : "#2563eb",
        }))
    : [];
  if (!treatments.length || !lanes.length) return null;
  return {
    name: parsed.name.slice(0, 200),
    bookingMode: parsed.bookingMode as BookingMode,
    treatments,
    lanes,
  };
}

export function parseWebsiteProposal(raw: string | undefined | null): WebsiteProposal | null {
  const parsed = parseJson(raw) as { themeId?: unknown } | null;
  if (!parsed || !isNonEmptyString(parsed.themeId)) return null;
  return { themeId: parsed.themeId as WebsiteThemeId };
}
